import { CircularProgress } from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useCallback, useState } from "react";
import { useReactFlow } from "reactflow";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { setIsReadyToLoadFlow, setLoaderProgressMessage } from "@common/reducers/ChatbotReducer";
import { saveBotThunk } from "@common-reducers/ChatbotThunks";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import BTbotbutton from "./bt-bot-button";

interface SaveButtonProps {
    disabled?: boolean;
    onBeforeSave?: () => boolean | Promise<boolean>;
    onAfterSave?: () => void;
    localCurrentBot?: any; // Type properly based on your bot type
    shouldClose?: boolean;
    sx?: any;
    label?: string;
    disableBigLoader?: boolean;
}

export const SaveButton = ({
    disabled = false,
    onBeforeSave,
    onAfterSave,
    localCurrentBot,
    shouldClose = false,
    sx = {},
    label,
    disableBigLoader = false
}: SaveButtonProps) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const reactFlowInstance = useReactFlow();
    const [isLoading, setIsLoading] = useState(false);
    const displayLabel = label || t('chatbot.saveDraft');

    const handleSave = useCallback(async () => {
        try {
            setIsLoading(true);

            if (onBeforeSave) {
                const canProceed = await onBeforeSave();
                if (!canProceed) {
                    setIsLoading(false);
                    return false;
                }
            }

            const viewport = reactFlowInstance.getViewport();
            if (!disableBigLoader) dispatch(setIsReadyToLoadFlow(false));
            dispatch(setLoaderProgressMessage(t('chatbot.savingBotDraft')));

            const result = await dispatch(saveBotThunk({
                viewport,
                shouldDeploy: false,
                localCurrentBot
            }));

            if (result.type.endsWith('/rejected')) {
                console.error('Save failed:', result.payload);
                dispatch(setLoaderProgressMessage(t('chatbot.errorWithMessage', { error: result.payload || t('chatbot.failedToSaveBot') })));
                setTimeout(() => dispatch(setLoaderProgressMessage(null)), 2000);
                return false;
            }

            if (shouldClose && onAfterSave) {
                onAfterSave();
            }

            return true;
        } catch (error) {
            console.error('Save operation failed:', error);
            const errorMessage = error instanceof Error ? error.message : t('chatbot.failedToSaveBot');
            dispatch(setLoaderProgressMessage(t('chatbot.errorWithMessage', { error: errorMessage })));
            setTimeout(() => dispatch(setLoaderProgressMessage(null)), 2000);
            return false;
        } finally {
            dispatch(setIsReadyToLoadFlow(true));
            setIsLoading(false);
        }
    }, [dispatch, reactFlowInstance, localCurrentBot, onBeforeSave, onAfterSave, shouldClose]);

    return (
        <BTbotbutton
            onClick={handleSave}
            disabled={disabled || isLoading}
            sx={{
                minWidth: '120px',
                position: 'relative',
                '& .MuiCircularProgress-root': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                },
                ...sx
            }}
        >
            {isLoading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
            ) : (
                displayLabel
            )}
        </BTbotbutton>
    );
}; 
